import { useState, useEffect, useRef } from "react";
import {
  Layout,
  Menu,
  Typography,
  Avatar,
  Badge,
  Space,
  Button,
  Dropdown,
  ConfigProvider,
  Modal,
} from "antd";
import {
  ProjectOutlined,
  ImportOutlined,
  DollarOutlined,
  AuditOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router";
import { NotificationPanel } from "./NotificationPanel";
import { useNotifications } from "../hooks/useNotifications";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { setPendingUserCount } from "../store/slices/pendingUsersSlice";
import { APPROVAL_USERS_API_PROXY_URL } from "../config/constants";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import DashboardTwoToneIcon from "@mui/icons-material/DashboardTwoTone";
import "../styles/trackon.css";
import "../styles/trackon-responsive.css";
import ErrorBoundary from "./ErrorBoundary/ErrorBoundary";
import { clearSession, hasValidSession, setAuthNotice } from "../utils/session";
import AccessRestricted from "./AccessRestricted/AccessRestricted";

// Management pages: only ADMIN, PMO and ACCOUNT MANAGER roles may open them.
const RESTRICTED_PAGE_PATHS = [
  "/watchtower/admin/users",
  "/watchtower/accounts",
  "/watchtower/configuration",
  "/watchtower/settings",
  "/watchtower/import",
  "/watchtower/security-questions",
];

const SESSION_WARNING_SECONDS = 5 * 60;
import { apiFetch } from "../utils/apiClient";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const BASE_MENU_ITEMS = [
  { key: "/watchtower/dashboard", icon: <GridViewOutlinedIcon style={{ fontSize: 16 }} />, label: "Dashboard" },
  { key: "/watchtower/projects", icon: <ProjectOutlined />, label: "Projects" },
  { key: "/watchtower/import", icon: <ImportOutlined />, label: "Data Import" },
  { key: "/watchtower/tracker", icon: <DollarOutlined />, label: "Milestone & Invoice" },
  { key: "/watchtower/review-list", icon: <AuditOutlined />, label: "Project Review" },
  { key: "/watchtower/admin/users", icon: <ManageAccountsOutlinedIcon style={{ fontSize: 18 }} />, label: "User Management" },
  { key: "/watchtower/settings", icon: <SettingOutlined />, label: "Settings" },
];

const PAGE_TITLES = {
  "/watchtower/dashboard": { title: "Project Dashboard", subtitle: "Consolidated view of all active projects, health status, and portfolio metrics" },
  "/watchtower/projects": { title: "Projects", subtitle: "Browse, manage, and track all projects across departments and delivery teams" },
  "/watchtower/import": { title: "Data Import", subtitle: "Bulk import project setup and financial data from structured Excel templates" },
  "/watchtower/tracker": { title: "Milestone & Invoice Trackers", subtitle: "Unified view of all milestones, invoice submissions, and payment status" },
  "/watchtower/review": { title: "Project Review", subtitle: "Review of individual projects with health, risk, and action items" },
  "/watchtower/review-list": { title: "Project Health Review Dashboard", subtitle: "Portfolio-level project health, risk visibility, and action-ready status insights" },
  "/watchtower/admin/users": { title: "User Management", subtitle: "Review pending registrations, assign roles, and manage user access" },
  "/watchtower/profile": { title: "My Profile", subtitle: "View and update your account information and preferences" },
  "/watchtower/settings": { title: "Settings", subtitle: "Manage application configurations and administrative settings." },
  "/watchtower/notifications": { title: "Notifications", subtitle: "Stay updated on project alerts, approvals, and action items" },
  "/watchtower/accounts": { title: "Accounts", subtitle: "Manage account settings and preferences" },
  "/watchtower/security-questions": { title: "Security Questions", subtitle: "Manage your security questions and account recovery options" },
  "/watchtower/configuration": { title: "Configuration", subtitle: "Manage conversion and currency-to-prefix mappings" },
  "/watchtower/leadership-attention": { title: "Leadership Attention View", subtitle: "Projects that require leadership review and follow-up." },
};

export default function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia("(max-width: 767px)").matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = (event) => setIsMobile(event.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  const [isSettingsFabHovered, setIsSettingsFabHovered] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const sessionRole = sessionStorage.getItem("role") || "";
  const effectiveRole = (sessionRole || "").toUpperCase();
  const isAdmin = effectiveRole === "ADMIN";
  const isPMO = effectiveRole === "PMO";
  const isAccountManager = effectiveRole === "ACCOUNT MANAGER";

  const pendingUserCount = useAppSelector(
    (state) => state.pendingUsers.count
  );

  const {
    notifications,
    unreadCount: unread,
    markRead,
    markAllRead,
    dismiss,
  } = useNotifications({ poll: true });

  const onLogout = () => {
    sessionStorage.clear();
    setAuthNotice("info", "You have been signed out. Please sign in again to continue.");
    navigate("/watchtower");
  };

  useEffect(() => {
    if (!isAdmin && !isPMO && !isAccountManager) {
      return;
    }

    const fetchPendingUsersCount = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await apiFetch(APPROVAL_USERS_API_PROXY_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }, "Failed to fetch pending users count.");

        if (!response.ok) return;

        const data = await response.json();
        const users = Array.isArray(data) ? data : data.data || [];

        const pendingCount = users.filter(
          (u) => String(u.status || "").toUpperCase() !== "ACTIVE"
        ).length;

        dispatch(setPendingUserCount(pendingCount));
      } catch (error) {
        console.error("Failed to fetch pending users count:", error);
      }
    };

    void fetchPendingUsersCount();
  }, [dispatch, isAdmin, isPMO, isAccountManager,location.pathname]);

  const menuItems = BASE_MENU_ITEMS
    .filter(
      (item) =>
        (item.key !== "/watchtower/admin/users" || isAdmin || isPMO || isAccountManager) &&
        (item.key !== "/watchtower/settings" || ((isAdmin || isPMO || isAccountManager) && isMobile)) &&
        (item.key !== "/watchtower/import" || isAdmin || isPMO || isAccountManager)
    )
    .map((item) => {
      if (item.key === "/watchtower/admin/users") {
        return {
          ...item,
          label: (
            <Space size={4}>
              <span>{item.label}</span>
              {pendingUserCount > 0 && (
                <Badge
                  count={pendingUserCount}
                  size="small"
                  style={{
                    marginLeft: 4,
                    backgroundColor: "#ffffff",
                    color: "#0958d9",
                    border: "1px solid #0958d9",
                    fontSize: "10px",
                    height: "15px",
                    width: "15px",
                    fontWeight: "800",
                    marginTop: "3px",
                    boxShadow: "none",
                  }}
                />
              )}
            </Space>
          ),
        };
      }
      return item;
    });

  const selectedKey =
    menuItems.find((item) => item.key !== "/" && location.pathname.startsWith(item.key))?.key ||
    (location.pathname === "/watchtower/dashboard" ? "/watchtower/dashboard" : "");
  const isReviewListPage = location.pathname.startsWith("/watchtower/review-list");
  const canOpenManagementPages = isAdmin || isPMO || isAccountManager;
  const isRestrictedPage =
    !canOpenManagementPages &&
    RESTRICTED_PAGE_PATHS.some((path) => location.pathname.startsWith(path));


  const currentPageKey =
    Object.keys(PAGE_TITLES).find(
      (k) => k !== "/watchtower/dashboard" && location.pathname.startsWith(k)
    ) ?? "/watchtower/dashboard";
  const currentPage =
    PAGE_TITLES[currentPageKey] ?? PAGE_TITLES["/watchtower/dashboard"];

  useEffect(() => {
    document.title = `${currentPage.title} | Watch Tower`;
  }, [currentPage.title]);

  // Protected pages require a session; send signed-out users to login and remember where they were going.
  const hasSession = hasValidSession();

  useEffect(() => {
    if (!hasSession) {
      if (sessionStorage.getItem("token")) {
        setAuthNotice("warning", "Your session has expired. Please sign in again.");
      }
      clearSession();
      navigate("/watchtower", {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    }
  }, [hasSession, navigate, location.pathname, location.search]);

  // Tracks the token expiry: warns shortly before it, and signs the user out when it passes.
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [sessionWarningDismissed, setSessionWarningDismissed] = useState(false);

  useEffect(() => {
    const checkExpiry = () => {
      const expiry = Number(sessionStorage.getItem("tokenExpiryInSeconds"));
      if (!expiry) {
        setSecondsLeft(null);
        return;
      }

      const remaining = expiry - Math.floor(Date.now() / 1000);
      setSecondsLeft(remaining);

      if (remaining <= 0) {
        clearSession();
        setAuthNotice("warning", "Your session has expired. Please sign in again.");
        navigate("/watchtower", { replace: true });
      }
    };

    checkExpiry();
    const intervalId = setInterval(checkExpiry, 15000);
    return () => clearInterval(intervalId);
  }, [navigate]);

  const showSessionWarning =
    secondsLeft !== null &&
    secondsLeft > 0 &&
    secondsLeft <= SESSION_WARNING_SECONDS &&
    !sessionWarningDismissed;

  if (!hasSession) {
    return null;
  }

  const capitalizeFirstLetter = (value) => {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const firstName = capitalizeFirstLetter(sessionStorage.getItem("firstName") || "");
  const lastName = capitalizeFirstLetter(sessionStorage.getItem("lastName") || "");
  const displayName = `${firstName} ${lastName}`.trim() || "";
  const displayRole = sessionRole || "";

  const adminProfileItems = isAdmin ? [] : [];

  const userMenuItems = [
    {
      key: "user-header",
      icon: <UserOutlined />,
      label: (
        <div>
          <div style={{ fontWeight: 600 }}>{displayName}</div>
          <div style={{ fontSize: 11, color: "#8c8c8c" }}>
            {sessionStorage.getItem("email")}
          </div>
        </div>
      ),
      disabled: true,
    },
    { type: "divider", key: "divider-1" },
    { key: "profile", icon: <UserOutlined />, label: "My Profile" },
    ...adminProfileItems,
    { type: "divider", key: "divider-2" },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: <span style={{ color: "#000" }}>Sign Out</span>,
      danger: true,
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#1677ff",
        },
      }}
    >
      <div className="trackon-root">
        <Layout style={{ minHeight: "100vh" }}>
          <Modal
            open={showSessionWarning}
            title="Your session is ending soon"
            okText="Sign out now"
            cancelText="Dismiss"
            closable={false}
            onOk={onLogout}
            onCancel={() => setSessionWarningDismissed(true)}
          >
            {`Your session ends in ${Math.max(1, Math.ceil((secondsLeft || 0) / 60))} minute(s). Save your work, then sign in again to keep going.`}
          </Modal>

          {mobileNavOpen && (
            <div
              className="trackon-nav-backdrop trackon-nav-backdrop--open"
              onClick={() => setMobileNavOpen(false)}
            />
          )}

          <Sider
            className={`trackon-sider${mobileNavOpen ? " trackon-sider--open" : ""}`}
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={221}
            style={{
              background: "#001529",
              boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
              position: "fixed",
              height: "100vh",
              left: 0,
              top: 0,
              zIndex: 100,
              overflow: "hidden",
            }}
          >
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <div
                style={{
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: collapsed ? "center" : "flex-start",
                  padding: collapsed ? "0 8px" : "0 12px 0 20px",
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  flexShrink: 0,
                  gap: 8,
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    background: "linear-gradient(135deg, #0958d9, #0958d9)",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(22,119,255,0.4)",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/watchtower/dashboard")}
                >
                  <DashboardTwoToneIcon style={{ color: "white", fontSize: 18 }} />
                </div>
                {!collapsed && (
                  <div
                    style={{ marginLeft: 4, flex: 1, cursor: "pointer" }}
                    onClick={() => navigate("/watchtower/dashboard")}
                  >
                    <Text style={{ color: "white", fontWeight: 700, fontSize: 16, display: "block", lineHeight: 1.2 }}>
                      Watch Tower
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>
                      Plan. Track. Deliver.
                    </Text>
                  </div>
                )}
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                  onClick={() => setCollapsed(!collapsed)}
                  style={{ color: "rgba(255,255,255,0.65)", fontSize: 16, flexShrink: 0 }}
                />
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                <Menu
                  theme="dark"
                  mode="inline"
                  selectedKeys={[selectedKey]}
                  items={menuItems}
                  onClick={({ key }) => {
                    setMobileNavOpen(false);
                    navigate(key);
                  }}
                  style={{ borderRight: 0, marginTop: 8 }}
                />
              </div>
            </div>
          </Sider>

          <Layout
            className="trackon-main"
            style={{
              marginLeft: collapsed ? 80 : 224,
              transition: "margin-left 0.2s",
              background: "#eef2f9",
              border: "1px solid rgb(229, 234, 243)",
            }}
          >
            <Header
              className="trackon-header"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 99,
                background: "#fff",
                padding: "0 24px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Button
                className="trackon-mobile-nav-toggle"
                type="text"
                icon={<MenuOutlined />}
                aria-label="Open navigation menu"
                onClick={() => {
                  setCollapsed(false);
                  setMobileNavOpen((open) => !open);
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography.Title level={4} style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#3c3c3e", lineHeight: 1.3 }}>
                  {currentPage.title}
                </Typography.Title>
                {currentPage.subtitle && (
                  <Text style={{ fontSize: 12, color: "#8b92a5", lineHeight: 1.2 }}>
                    {currentPage.subtitle}
                  </Text>
                )}
              </div>

              <Space size={12}>
                <div ref={bellRef} style={{ position: "relative" }}>
                  <button
                    onClick={() => setNotifOpen((o) => !o)}
                    style={{
                      position: "relative",
                      color: notifOpen ? "#0078d4" : "#5c6070",
                      background: notifOpen ? "#e8f4fd" : "none",
                      border: "1px solid",
                      borderColor: notifOpen ? "#b3d4f5" : "transparent",
                      cursor: "pointer",
                      padding: "5px 6px",
                      borderRadius: 6,
                      display: "flex",
                      transition: "all 0.15s",
                    }}
                    title="Notifications"
                    aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
                  >
                    <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5S10.5 3.17 10.5 4v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
                    </svg>
                    {unread > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -3,
                          right: -3,
                          minWidth: 17,
                          height: 17,
                          background: "#cf1322",
                          borderRadius: 9,
                          fontSize: 9,
                          fontWeight: 800,
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "0 4px",
                          border: "2px solid #fff",
                        }}
                      >
                        {unread}
                      </span>
                    )}
                  </button>
                  {notifOpen && (
                    <NotificationPanel
                      notifications={notifications}
                      onMarkRead={markRead}
                      onMarkAllRead={markAllRead}
                      onDismiss={dismiss}
                      onClose={() => setNotifOpen(false)}
                      onViewAll={() => navigate("/watchtower/notifications")}
                    />
                  )}
                </div>

                <Dropdown
                  menu={{
                    items: userMenuItems,
                    onClick: ({ key }) => {
                      if (key === "logout") onLogout();
                      if (key === "profile") navigate("/watchtower/profile");
                      if (key === "accounts") navigate("/watchtower/accounts");
                      if (key === "configuration") navigate("/watchtower/configuration");
                      if (key === "security-questions") navigate("/watchtower/security-questions");
                    },
                  }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Space style={{ cursor: "pointer", padding: "4px 8px", borderRadius: 8 }}>
                    <Avatar style={{ background: "#0958d9", fontSize: 14 }}>
                      {displayName ? displayName.charAt(0).toUpperCase() : ""}
                    </Avatar>
                    <div className="trackon-user-meta" style={{ lineHeight: 1.2 }}>
                      <Text style={{ display: "block", fontWeight: 600, fontSize: 13 }}>
                        {displayName}
                      </Text>
                      <Text style={{ color: "#8c8c8c", fontSize: 11 }}>{displayRole}</Text>
                    </div>
                  </Space>
                </Dropdown>
              </Space>
            </Header>

            <Content
              className={isReviewListPage ? undefined : "trackon-content"}
              style={{
                minHeight: "calc(100vh - 112px)",
                margin: isReviewListPage ? 0 : 24,
              }}
            >
              {isRestrictedPage ? (
                <AccessRestricted />
              ) : (
                <ErrorBoundary>{children}</ErrorBoundary>
              )}
            </Content>
          </Layout>

          {(isAdmin || isPMO || isAccountManager) && (
            <Button
              type="default"
              onClick={() => navigate("/watchtower/settings")}
              onMouseEnter={() => setIsSettingsFabHovered(true)}
              onMouseLeave={() => setIsSettingsFabHovered(false)}
              onFocus={() => setIsSettingsFabHovered(true)}
              onBlur={() => setIsSettingsFabHovered(false)}
              aria-label="Settings"
              className="trackon-settings-fab"
              style={{
                position: "fixed",
                left: 16,
                bottom: 16,
                zIndex: 120,
                width: isSettingsFabHovered ? 156 : 50,
                height: 50,
                borderRadius: 999,
                border: "none",
                background: "linear-gradient(135deg, #1677ff, #0958d9)",
                color: "#ffffff",
                boxShadow: isSettingsFabHovered
                  ? "0 12px 32px rgba(22,119,255,0.35)"
                  : "0 8px 20px rgba(22,119,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 10,
                overflow: "hidden",
                cursor: "pointer",
                transform: isSettingsFabHovered ? "translateY(-2px)" : "translateY(0)",
                transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <SettingOutlined
                style={{
                  fontSize: 19,
                  minWidth: 19,
                  color: "#ffffff",
                }}
              />
              <span
                style={{
                  opacity: isSettingsFabHovered ? 1 : 0,
                  transform: isSettingsFabHovered ? "translateX(0)" : "translateX(-10px)",
                  transition: "all 300ms ease",
                  whiteSpace: "nowrap",
                  fontWeight: 400,
                  fontSize: 15,
                  color: "#ffffff",
                  pointerEvents: "none",
                }}
              >
                Settings
              </span>
            </Button>
          )}
        </Layout>
      </div>
    </ConfigProvider>
  );
}
